import { Aperture, CalendarBlank } from '@phosphor-icons/react';
import { Loader, semanticColors, VText } from '@vake/ui-kit';
import { Feature, FeatureCollection } from 'geojson';
import { CirclePaint, FillPaint, LinePaint, RasterPaint, ResourceType, SymbolLayout } from 'mapbox-gl';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Layer, Map, MapLayerMouseEvent, MapRef, NavigationControl, ScaleControl, Source } from 'react-map-gl';
import { useAuth } from '../../auth/AuthProvider';
import { useOverwatchContext } from '../../pages/Report/interactiveReport/overwatch-2-context';
import { useTimeAtPointInAcqusition } from '../../requests/reports';
import { apiBaseUrl } from '../../utils/env';
import { FeatureCollectionAcquisitions } from '../../utils/interfaces';
import { FLY_TO_DURATION, INSIGHT_ZOOM_LEVEL, WEB_MAP_TILE_SERVICE_MIN_ZOOM } from '../constants';
import { OverwatchPopup } from './Popup';

type AcquisitionsView = {
    timestamp: Date;
    satellite: string | null;
    lat: number;
    lon: number;
};
const AcquisitionsView: FC<AcquisitionsView> = ({ timestamp, satellite, lon, lat }) => {
    const { data: timeAtPoint, isLoading, isError } = useTimeAtPointInAcqusition(timestamp, lon, lat);
    const text = timeAtPoint
        ? `${timeAtPoint.toISOString().slice(0, 10)} ${timeAtPoint.toISOString().slice(11, 16)}`
        : 'Within the next hours';
    if (isLoading) {
        return (
            <div className="flex justify-center">
                <Loader size={16} />
            </div>
        );
    }
    if (isError) {
        return <VText variant="D1">Did not find time acqusition times at point</VText>;
    }
    return (
        <div>
            <VText variant="H4">Satellite pass</VText>
            <div className="flex items-center gap-2">
                <Aperture size={16} />
                <VText variant="D1">Satellite: {satellite}</VText>
            </div>
            <div className="flex items-center gap-2">
                <CalendarBlank size={16} />
                <VText variant="D1">Time: {text}</VText>
            </div>
        </div>
    );
};

type CircleLayer = {
    id: string;
    type: 'circle';
    data: FeatureCollection;
    paint: CirclePaint;
};
type LineLayer = {
    id: string;
    type: 'line';
    data: FeatureCollection;
    paint: LinePaint;
};
type FillLayer = {
    id: string;
    type: 'fill';
    data: FeatureCollection | FeatureCollectionAcquisitions;
    paint: FillPaint;
    static?: boolean;
    maxzoom?: number;
};
type SymbolLayer = {
    id: string;
    type: 'symbol';
    data: FeatureCollection;
    layout: SymbolLayout;
};

const addLayer = (layers: CircleLayer[] | LineLayer[] | FillLayer[] | SymbolLayer[]) => {
    return layers.map((layer) => {
        return (
            <Source key={layer.id} id={layer.id} type="geojson" data={layer.data}>
                <Layer
                    id={layer.id}
                    type={layer.type}
                    {...('paint' in layer ? { paint: layer.paint } : {})}
                    {...('layout' in layer ? { layout: layer.layout } : {})}
                    {...('static' in layer ? { static: layer.static } : {})}
                    {...('maxzoom' in layer ? { maxzoom: layer.maxzoom } : {})}
                />
            </Source>
        );
    });
};

const focusedFeaturePaint = {
    'circle-radius': 12,
    'circle-stroke-color': semanticColors.warning.hover,
    'circle-stroke-width': 2,
    'circle-opacity': 0.2,
    'circle-stroke-opacity': 0.4,
    'circle-color': semanticColors.warning.default,
};

type RMap2 = {
    activeDate: Date | undefined;
    availableDates: Date[] | undefined;
    selectedFeature: Feature | null;
    setSelectedFeature: (feature: Feature | null) => void;
    setHoveredFeature: (feature: Feature | null) => void;
    hoveredFeature: Feature | null;
    darkVessels: FeatureCollection;
    matchedVessels: FeatureCollection;
    undetectedVessels: FeatureCollection;
    windmills: FeatureCollection;
    platforms: FeatureCollection;
    aoi: FeatureCollection;
    footprints: FeatureCollection[];
    acquisitions: FeatureCollectionAcquisitions | undefined;
};
export const RMap2: React.FC<RMap2> = ({
    selectedFeature,
    setSelectedFeature,
    setHoveredFeature,
    hoveredFeature,
    darkVessels,
    matchedVessels,
    undetectedVessels,
    windmills,
    platforms,
    aoi,
    footprints,
    acquisitions,
}) => {
    const {
        settings: { showEEZ, showSatelliteImage },
        filters: { startDate: date },
        mapView,
    } = useOverwatchContext();
    const { latitude, longitude, zoom } = mapView;
    const [overwatchPopup, setOverwatchPopup] = useState<OverwatchPopup | null>(null);
    const { token } = useAuth();
    const [_, setMap] = useState<MapRef | null>(null);
    const mapRef = React.useRef<MapRef | null>(null);
    useEffect(() => {
        if (mapRef.current) {
            mapRef.current?.flyTo({
                center: { lon: longitude, lat: latitude },
                duration: FLY_TO_DURATION,
                zoom: zoom,
            });
        }
    }, [mapRef, longitude, latitude, zoom]);
    const setPopup = (event: MapLayerMouseEvent, content?: ReactNode) => {
        setTimeout(() => {
            const parsedData = {
                lon: event.lngLat.lng,
                lat: event.lngLat.lat,
                content,
            };
            setOverwatchPopup(overwatchPopup ? null : parsedData);
        }, 0.01);
    };
    const onClick = (event: MapLayerMouseEvent) => {
        if (event.features && event.features.length > 0) {
            const clickedFeature = event.features[0];
            const source = clickedFeature.source;
            if (source === 'matchedVessels') {
                setSelectedFeature(matchedVessels.features.find((f) => f.id === clickedFeature.id?.toString()) ?? null);
            } else if (source === 'darkVessels') {
                setSelectedFeature(darkVessels.features.find((f) => f.id === clickedFeature.id?.toString()) ?? null);
            } else if (source === 'undetectedVessels') {
                setSelectedFeature(
                    undetectedVessels.features.find((f) => f.id === clickedFeature.id?.toString()) ?? null
                );
            } else if (source === 'windmills') {
                setSelectedFeature(windmills.features.find((f) => f.id === clickedFeature.id?.toString()) ?? null);
            } else if (source === 'platforms') {
                setSelectedFeature(platforms.features.find((f) => f.id === clickedFeature.id?.toString()) ?? null);
            } else if (source === 'acqusitions') {
                const allFeatures = mapRef.current?.queryRenderedFeatures(event.point);
                if (allFeatures?.some((feature) => feature.layer.id.startsWith('footprints-'))) {
                    setPopup(event);
                } else {
                    setPopup(
                        event,
                        <AcquisitionsView
                            timestamp={
                                clickedFeature.properties?.startTime
                                    ? new Date(clickedFeature.properties.startTime)
                                    : new Date()
                            }
                            satellite={
                                clickedFeature.properties?.satellite ? clickedFeature.properties.satellite : null
                            }
                            lat={event.lngLat.lat}
                            lon={event.lngLat.lng}
                        />
                    );
                }
            }
        } else {
            setPopup(event);
        }
    };
    const onHover = useCallback(
        (event: MapLayerMouseEvent) => {
            if (event.features && event.features.length > 0) {
                const hoveredFeature = event.features[0];
                const source = hoveredFeature.source;
                if (source === 'matchedVessels') {
                    setHoveredFeature(
                        matchedVessels.features.find((f) => f.id === hoveredFeature.id?.toString()) ?? null
                    );
                } else if (source === 'darkVessels') {
                    setHoveredFeature(darkVessels.features.find((f) => f.id === hoveredFeature.id?.toString()) ?? null);
                } else if (source === 'undetectedVessels') {
                    setHoveredFeature(
                        undetectedVessels.features.find((f) => f.id === hoveredFeature.id?.toString()) ?? null
                    );
                } else if (source === 'windmills') {
                    setHoveredFeature(windmills.features.find((f) => f.id === hoveredFeature.id?.toString()) ?? null);
                } else if (source === 'platforms') {
                    setHoveredFeature(platforms.features.find((f) => f.id === hoveredFeature.id?.toString()) ?? null);
                } else {
                    setHoveredFeature(null);
                }
            } else {
                setHoveredFeature(null);
            }
        },
        [
            setHoveredFeature,
            matchedVessels.features,
            darkVessels.features,
            undetectedVessels.features,
            windmills.features,
            platforms.features,
        ]
    );
    const onMapLoad = useCallback(() => {
        if (!mapRef.current) return;
        mapRef.current.loadImage(
            '/images/icons/ArrowRight.png',
            (error: Error | undefined, result: HTMLImageElement | ImageBitmap | undefined) => {
                if (error) {
                    throw error;
                }
                if (mapRef.current && result) {
                    mapRef.current.addImage('arrow-right', result);
                }
            }
        );
        setMap(mapRef.current);
    }, [setMap]);
    useEffect(() => {
        if (selectedFeature) {
            const lat = selectedFeature.properties?.lat;
            const lon = selectedFeature.properties?.lon;
            if (mapRef.current && lat && lon) {
                const bounds = mapRef.current.getBounds();
                if (!(bounds.contains([lon, lat]) && mapRef.current.getZoom() > INSIGHT_ZOOM_LEVEL - 1.5)) {
                    mapRef.current?.flyTo({
                        center: [lon, lat],
                        duration: FLY_TO_DURATION,
                        zoom: Math.max(mapRef.current.getZoom(), INSIGHT_ZOOM_LEVEL),
                    });
                }
            }
        }
    }, [selectedFeature]);
    const connectedVessels: FeatureCollection = useMemo(() => {
        const combinedFeatures = [
            ...matchedVessels.features,
            ...undetectedVessels.features,
            ...windmills.features,
            ...platforms.features,
        ];
        const groupByMMSI = combinedFeatures.reduce((acc, feature) => {
            const mmsi = feature.properties?.mmsi;
            if (mmsi) {
                acc[mmsi] = acc[mmsi] || [];
                acc[mmsi].push(feature);
            }
            return acc;
        }, {} as Record<string, Feature[]>);
        const lines: Feature[] = [];
        Object.values(groupByMMSI).forEach((features) => {
            if (features.length > 1) {
                lines.push({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: features
                            .sort((a, b) => {
                                const t1 = new Date(a.properties?.timestamp ?? 0).getTime();
                                const t2 = new Date(b.properties?.timestamp ?? 0).getTime();
                                return t1 - t2;
                            })
                            .map((f) => [f.properties?.lon, f.properties?.lat]),
                    },
                    properties: {},
                });
            }
        });
        return { type: 'FeatureCollection', features: lines };
    }, [matchedVessels.features, platforms.features, undetectedVessels.features, windmills.features]);
    const aoiLayerId = 'aoi';
    const lineLayers: LineLayer[] = useMemo(() => {
        return [
            {
                id: aoiLayerId,
                type: 'line',
                paint: {
                    'line-color': semanticColors['primary-action'].button,
                },
                data: aoi,
            },
            {
                id: 'connected-vessel-lines',
                type: 'line',
                paint: {
                    'line-color': semanticColors.success.background,
                    'line-width': 2,
                    'line-opacity': 0.8,
                },
                data: connectedVessels,
            },
        ];
    }, [aoi, connectedVessels]);
    const symbolLayers: SymbolLayer[] = useMemo(() => {
        return [
            {
                id: 'connected-vessel-arrows',
                type: 'symbol',
                layout: {
                    'symbol-placement': 'line',
                    'icon-image': 'arrow-right',
                    'icon-size': 0.5,
                },
                data: connectedVessels,
            },
        ];
    }, [connectedVessels]);
    const fillLayers: FillLayer[] = useMemo(() => {
        const defaultFillLayers: FillLayer[] = footprints.map((footprint, index) => ({
            id: `footprints-${index}`,
            static: true,
            data: footprint,
            paint: {
                'fill-color': [
                    'match',
                    ['get', 'processing_status'],
                    'completed',
                    semanticColors.success.default,
                    'started',
                    semanticColors.warning.default,
                    'waiting',
                    semanticColors.warning.background,
                    'failed',
                    semanticColors.danger.background,
                    semanticColors['primary-action'].graphical, // Default color in overwatch without status-coloring
                ],
                'fill-opacity': 0.3,
            },
            type: 'fill',
            maxzoom: showSatelliteImage ? WEB_MAP_TILE_SERVICE_MIN_ZOOM : 20,
        }));
        if (acquisitions) {
            defaultFillLayers.push({
                id: 'acqusitions',
                data: acquisitions,
                paint: { 'fill-color': semanticColors['system-fill'].secondary, 'fill-opacity': 0.25 },
                type: 'fill',
            });
        }
        return defaultFillLayers;
    }, [acquisitions, footprints, showSatelliteImage]);
    const circleLayers: CircleLayer[] = useMemo(() => {
        return [
            {
                id: 'windmills',
                data: windmills,
                paint: {
                    'circle-color': semanticColors.cyan.default,
                    'circle-stroke-color': '#0171A4', // TODO
                    'circle-stroke-width': 1,
                },
                type: 'circle',
            },
            {
                id: 'platforms',
                data: platforms,
                paint: {
                    'circle-color': semanticColors.mint.default,
                    'circle-stroke-color': '#0D817C', // TODO
                    'circle-stroke-width': 1,
                },
                type: 'circle',
            },
            {
                id: 'undetectedVessels',
                type: 'circle',
                paint: {
                    'circle-color': semanticColors['primary-action'].button,
                    'circle-stroke-color': semanticColors['primary-action'].background,
                    'circle-stroke-width': 1,
                },
                data: undetectedVessels,
            },
            {
                id: 'darkVessels',
                type: 'circle',
                paint: {
                    'circle-color': semanticColors.danger.default,
                    'circle-stroke-color': semanticColors.danger.background,
                    'circle-stroke-width': 1,
                },
                data: darkVessels,
            },
            {
                id: 'matchedVessels',
                type: 'circle',
                paint: {
                    'circle-color': semanticColors.success.default,
                    'circle-stroke-color': semanticColors.success.background,
                    'circle-stroke-width': 1,
                },
                data: matchedVessels,
            },
        ];
    }, [darkVessels, matchedVessels, platforms, undetectedVessels, windmills]);
    const transformRequest = (url: string, resourceType: ResourceType) => {
        // this is for authenticating to VAKE tileserver
        if (resourceType === 'Tile' && url.match(apiBaseUrl())) {
            return {
                url: url,
                headers: { Authorization: 'Bearer ' + token },
            };
        } else {
            return { url: url };
        }
    };

    const activeDateShortForm = date ? date.toISOString().split('T')[0] : '';
    const eezLayer: {
        id: string;
        type: 'vector';
        geometryType: 'line';
        sourceLayer: 'eez_v12_boundaries';
        paint: LinePaint;
        tiles: string[];
        minzoom: number;
        maxzoom: number;
    } = {
        id: 'eez-layer',
        type: 'vector',
        geometryType: 'line',
        paint: {
            'line-color': semanticColors['primary-action']['background-2'],
            'line-opacity': 1,
            'line-width': 1,
        },
        sourceLayer: 'eez_v12_boundaries',
        tiles: [apiBaseUrl() + '/tiles/eez/{z}/{x}/{y}.pbf'],
        minzoom: 0,
        maxzoom: 18,
    };
    const seamarkLayer: {
        id: string;
        type: 'raster';
        paint: RasterPaint;
        tiles: string[];
        minzoom: number;
        maxzoom: number;
    } = {
        id: 'seamark-layer',
        type: 'raster',
        paint: {
            // Optional: tweak opacity or brightness if desired
            'raster-opacity': 1.0,
        },
        tiles: ['https://t1.openseamap.org/seamark/{z}/{x}/{y}.png'],
        minzoom: 0,
        maxzoom: 18,
    };
    const sentinelImageLayer: {
        id: string;
        type: 'raster';
        tiles: string[];
        minzoom: number;
    } = {
        id: 'sentinel-image',
        type: 'raster',
        minzoom: WEB_MAP_TILE_SERVICE_MIN_ZOOM,
        tiles: [
            `https://services.sentinel-hub.com/ogc/wmts/27b1fd96-7716-4fe4-909e-95c959f273de?showLogo=false&service=WMTS&request=GetTile&format=image/jpeg&layer=NATURAL-COLOR&transparent=false&maxcc=100&&time=${activeDateShortForm}/${activeDateShortForm}&TILEMATRIXSET=PopularWebMercator256&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}`,
        ],
    };
    return (
        <div className="w-full h-full">
            <Map
                initialViewState={mapView}
                ref={mapRef}
                onLoad={onMapLoad}
                mapStyle="mapbox://styles/vake-torstein/cl4xzn1gs000p14qgz8nqs0ms"
                mapboxAccessToken="pk.eyJ1IjoidmFrZS10b3JzdGVpbiIsImEiOiJjbDR4emJmbW0xdTRpM21tbG1lbnc3b2JrIn0.V7PXI4I2ndH2mxaJrVCnRw"
                interactiveLayerIds={[
                    'windmills',
                    'platforms',
                    'undetectedVessels',
                    'darkVessels',
                    'matchedVessels',
                    'acqusitions',
                ]}
                onClick={onClick}
                onMouseMove={onHover}
                transformRequest={transformRequest}
            >
                <ScaleControl />
                <NavigationControl position="bottom-right" />
                {overwatchPopup && <OverwatchPopup {...overwatchPopup} />}
                {addLayer(lineLayers)}
                {addLayer(fillLayers)}
                {addLayer(circleLayers)}
                {addLayer(symbolLayers)}
                {selectedFeature?.properties && (
                    <Source key="selectedFeature" id="selectedFeature" type="geojson" data={selectedFeature}>
                        <Layer id="selectedFeature" type="circle" paint={focusedFeaturePaint} />
                    </Source>
                )}
                {hoveredFeature?.properties && (
                    <Source key="hoveredFeature" id="hoveredFeature" type="geojson" data={hoveredFeature}>
                        <Layer id="hoveredFeature" type="circle" paint={focusedFeaturePaint} />
                    </Source>
                )}
                {showEEZ && (
                    <Source
                        id={eezLayer.id}
                        type={eezLayer.type}
                        tiles={eezLayer.tiles}
                        minzoom={eezLayer.minzoom}
                        maxzoom={eezLayer.maxzoom}
                    >
                        <Layer
                            id={eezLayer.id}
                            type={eezLayer.geometryType}
                            source-layer={eezLayer.sourceLayer}
                            paint={eezLayer.paint}
                        />
                    </Source>
                )}
                <Source
                    id={seamarkLayer.id}
                    type={seamarkLayer.type}
                    tiles={seamarkLayer.tiles}
                    minzoom={seamarkLayer.minzoom}
                    maxzoom={seamarkLayer.maxzoom}
                >
                    <Layer id={seamarkLayer.id} type={seamarkLayer.type} paint={seamarkLayer.paint} />
                </Source>
                {showSatelliteImage && (
                    <Source
                        id={sentinelImageLayer.id}
                        type={sentinelImageLayer.type}
                        tiles={sentinelImageLayer.tiles}
                        minzoom={sentinelImageLayer.minzoom}
                    >
                        <Layer
                            id={sentinelImageLayer.id}
                            type={sentinelImageLayer.type}
                            minzoom={sentinelImageLayer.minzoom}
                            beforeId={aoiLayerId}
                        />
                    </Source>
                )}
            </Map>
        </div>
    );
};
